'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';

const luxuryStyles = `
  @keyframes lux-fade-up {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes lux-shimmer {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes lux-float {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-4px); }
  }
  .lux-fade-up { animation: lux-fade-up 0.7s cubic-bezier(0.22,1,0.36,1) both; }
  .lux-fade-up-1 { animation: lux-fade-up 0.7s cubic-bezier(0.22,1,0.36,1) 0.1s both; }
  .lux-fade-up-2 { animation: lux-fade-up 0.7s cubic-bezier(0.22,1,0.36,1) 0.2s both; }
  .lux-fade-up-3 { animation: lux-fade-up 0.7s cubic-bezier(0.22,1,0.36,1) 0.3s both; }
  .lux-fade-up-4 { animation: lux-fade-up 0.7s cubic-bezier(0.22,1,0.36,1) 0.4s both; }
  .lux-fade-up-5 { animation: lux-fade-up 0.7s cubic-bezier(0.22,1,0.36,1) 0.5s both; }
  .lux-shimmer-text {
    background: linear-gradient(90deg, #c9a96e 0%, #f5e0a8 40%, #c9a96e 60%, #e8c98a 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: lux-shimmer 3.5s linear infinite;
  }
  .lux-float { animation: lux-float 4s ease-in-out infinite; }
`;

function FloralDivider() {
  return (
    <div className="flex items-center justify-center gap-3 my-6">
      <div className="h-px flex-1" style={{ background: 'linear-gradient(to right, transparent, #c9a96e)' }} />
      <span className="text-lg lux-float" style={{ color: '#c9a96e' }}>✦</span>
      <span className="text-sm" style={{ color: '#c9a96e' }}>❧</span>
      <span className="text-lg lux-float" style={{ color: '#c9a96e', animationDelay: '0.5s' }}>✦</span>
      <div className="h-px flex-1" style={{ background: 'linear-gradient(to left, transparent, #c9a96e)' }} />
    </div>
  );
}

export default function WeddingLandingPage() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#fdf6f0' }}>
      <style dangerouslySetInnerHTML={{ __html: luxuryStyles }} />

      {/* Hero Section */}
      <div className="w-full max-w-2xl mx-auto px-4 py-10 pb-6">

        {/* Ganesha */}
        <div className="flex justify-center mb-4 lux-fade-up">
          <Image
            src="/assets/images/Ganesha-1787000120210.png"
            alt="Shree Ganesha"
            width={70}
            height={84}
            unoptimized
            priority
            className="w-auto object-contain"
            style={{ maxHeight: '84px' }}
          />
        </div>

        <div className="text-center mb-2 lux-fade-up">
          <p className="font-display text-base font-semibold tracking-wider lux-shimmer-text">
            ॥ श्री गणेशाय नमः ॥
          </p>
        </div>

        {/* Logo */}
        <div className="flex justify-center mb-6 lux-fade-up-1">
          <Image
            src="/assets/images/Logo_Vimi-1786996384061.png"
            alt="Vimi Logo"
            width={120}
            height={80}
            unoptimized
            className="h-16 w-auto object-contain"
          />
        </div>

        {/* Couple Photo */}
        <div
          className="w-full rounded-3xl overflow-hidden shadow-2xl mb-6 lux-fade-up-1"
          style={{ border: '2px solid #c9a96e' }}
        >
          <Image
            src="/assets/images/Final_photo_2-1786996888211.jpg"
            alt="Vikas and Anamika together"
            width={600}
            height={800}
            unoptimized
            priority
            className="w-full object-contain"
            style={{ backgroundColor: '#fdf6f0' }}
          />
        </div>

        {/* Names */}
        <div className="text-center lux-fade-up-2">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase mb-3" style={{ color: '#c9a96e' }}>
            Together with their families
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-medium mb-1" style={{ color: '#3d2b1f' }}>
            Vikas
          </h1>
          <p className="text-sm mb-2" style={{ color: '#9b7b6b' }}>S/o Shri Manikrao Gawande</p>
          <p className="font-display text-2xl my-2 lux-shimmer-text">&amp;</p>
          <h1 className="font-display text-4xl sm:text-5xl font-medium mb-1" style={{ color: '#3d2b1f' }}>
            Anamika
          </h1>
          <p className="text-sm mb-4" style={{ color: '#9b7b6b' }}>D/o Late. Shri Kishorrao Panse</p>
          <p className="text-base tracking-widest uppercase font-semibold lux-shimmer-text">#Vimi</p>
        </div>

        <FloralDivider />

        {/* Wedding Events */}
        <div className="lux-fade-up-3 space-y-3 mb-6">
          <h2 className="text-center text-xs font-semibold tracking-[0.25em] uppercase mb-4" style={{ color: '#c9a96e' }}>
            Celebrations
          </h2>

          {/* Engagement */}
          <div
            className="rounded-2xl p-5"
            style={{ backgroundColor: 'rgba(255,248,240,0.95)', border: '1px solid rgba(201,169,110,0.4)' }}
          >
            <div className="flex items-start gap-4">
              <div className="text-2xl">💍</div>
              <div className="flex-1">
                <p className="font-semibold text-base mb-0.5" style={{ color: '#3d2b1f' }}>Engagement</p>
                <p className="font-display text-lg" style={{ color: '#c9a96e' }}>31 December 2026</p>
                <p className="text-sm font-medium mt-0.5" style={{ color: '#9b7b6b' }}>12:00 PM onwards</p>
                <p className="text-sm mt-0.5" style={{ color: '#9b7b6b' }}>Royal Celebrations, Bhopal, MP</p>
                <a
                  href="https://maps.app.goo.gl/o6Wyuiqrn5Wi5iZo7"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-2 text-sm font-medium hover:underline"
                  style={{ color: '#c9a96e' }}
                >
                  View on Maps →
                </a>
              </div>
            </div>
          </div>

          {/* Haldi & Sangeet */}
          <div
            className="rounded-2xl p-5"
            style={{ backgroundColor: 'rgba(255,248,240,0.95)', border: '1px solid rgba(201,169,110,0.4)' }}
          >
            <div className="flex items-start gap-4">
              <div className="text-2xl">🌸</div>
              <div className="flex-1">
                <p className="font-semibold text-base mb-0.5" style={{ color: '#3d2b1f' }}>Haldi &amp; Sangeet</p>
                <p className="font-display text-lg" style={{ color: '#c9a96e' }}>3 January 2027</p>
                <div className="mt-1 space-y-1">
                  <p className="text-sm font-medium" style={{ color: '#9b7b6b' }}>🌿 Haldi — 12:00 PM</p>
                  <p className="text-sm font-medium" style={{ color: '#9b7b6b' }}>🎶 Sangeet — 7:30 PM</p>
                </div>
                <p className="text-sm mt-0.5" style={{ color: '#9b7b6b' }}>Ramdevbaba Mangalam, Lawn, Warud, MH</p>
              </div>
            </div>
          </div>

          {/* Wedding */}
          <div
            className="rounded-2xl p-5"
            style={{ backgroundColor: 'rgba(255,240,230,0.95)', border: '1px solid rgba(201,169,110,0.5)' }}
          >
            <div className="flex items-start gap-4">
              <div className="text-2xl">❤️</div>
              <div className="flex-1">
                <p className="font-semibold text-base mb-0.5" style={{ color: '#3d2b1f' }}>Wedding</p>
                <p className="font-display text-lg" style={{ color: '#c9a96e' }}>4 January 2027</p>
                <p className="text-sm font-medium mt-0.5" style={{ color: '#9b7b6b' }}>10:30 AM onwards</p>
                <p className="text-sm mt-0.5" style={{ color: '#9b7b6b' }}>Ramdevbaba Mangalam, Lawn, Warud, MH</p>
                <a
                  href="https://maps.app.goo.gl/msDE3717cPhYMEgu5"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-2 text-sm font-medium hover:underline"
                  style={{ color: '#c9a96e' }}
                >
                  View on Maps →
                </a>
              </div>
            </div>
          </div>
        </div>

        <FloralDivider />

        {/* Invitation Links */}
        <div className="lux-fade-up-4 space-y-3 mb-6">
          <h2 className="text-center text-xs font-semibold tracking-[0.25em] uppercase mb-4" style={{ color: '#c9a96e' }}>
            View Invitations
          </h2>

          <Link
            href="/groom"
            className="flex items-center justify-between w-full rounded-2xl p-5 transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
            style={{
              background: 'linear-gradient(135deg, #c9a96e 0%, #e8c98a 100%)',
              color: '#fff',
            }}
          >
            <div>
              <p className="font-semibold text-base">Groom's Invitation</p>
              <p className="text-sm opacity-90 mt-0.5">Vikas Gawande · Bhopal Side</p>
            </div>
            <span className="text-2xl">💍</span>
          </Link>

          <Link
            href="/bride"
            className="flex items-center justify-between w-full rounded-2xl p-5 transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
            style={{
              background: 'linear-gradient(135deg, #d4a0b0 0%, #e8c0cc 100%)',
              color: '#fff',
            }}
          >
            <div>
              <p className="font-semibold text-base">Bride's Invitation</p>
              <p className="text-sm opacity-90 mt-0.5">Anamika Panse · Warud Side</p>
            </div>
            <span className="text-2xl">🌸</span>
          </Link>
        </div>

        <FloralDivider />

        {/* Hashtag & Footer */}
        <div className="text-center lux-fade-up-5 mb-8">
          <p className="font-display text-2xl lux-shimmer-text mb-2">#Vimi</p>
          <p className="text-sm" style={{ color: '#9b7b6b' }}>31 Dec 2026 – 4 Jan 2027</p>
          <p className="text-xs mt-1" style={{ color: '#c9a96e' }}>
            Bhopal &amp; Warud
          </p>
        </div>

        {/* Admin Login — subtle at bottom */}
        <div className="text-center pb-6">
          <Link
            href="/admin"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-medium transition-all duration-200 hover:opacity-80"
            style={{
              backgroundColor: 'rgba(255,255,255,0.6)',
              color: '#9b7b6b',
              border: '1px solid rgba(201,169,110,0.3)',
            }}
          >
            🔐 Admin Login
          </Link>
        </div>
      </div>
    </div>
  );
}
